<template>
    <div :class="{ disabled: loading || paymentChecking }" class="public-offer-order">
        <default-title class="mb-20" :size="16" large :line-height="21">
            {{ t("payments.public-offer.order") }}
        </default-title>

        <PublicOfferOrderItem :key="index" v-for="(offer, index) in order.selectedOffers" :offer="offer" />

        <PublicOfferOrderPrice
            :order="order"
            :payment="payment"
            :payment-parts="paymentParts"
            :show-partial-price="showPartialPrice"
        />

        <PublicOfferCouponInput
            @set:orderId="$emit('set:orderId', $event)"
            @update:order="$emit('update')"
            v-if="order && allowCoupons"
            :order="order"
        />

        <PublicOfferPoints
            @update:order="$emit('update')"
            class="mt-30"
            v-if="order && allowPoints && auth && order.available_points"
            :order="order"
        />

        <PublicOfferOrderDiscount
            v-if="order.discount && order.discount.end_type !== 3"
            :key="`discount-update-${loading}`"
            :until-date="order.discount.end_at_utc"
            class="mt-10 mb-20"
            @update:order="$emit('update')"
        />

        <div v-html="help"></div>

        <AuthorizationDynamicFields
            v-if="order.fields && order.fields.length"
            :key="`fields-update-${loading}`"
            :errors="errors"
            :fields="order.fields"
            class="mt-20"
        />

        <div class="order-payment">
            <PublicOfferPrependPayment
                v-if="payment"
                @handleAuth="handleAuth"
                @handlePaymentLinks="handlePaymentLinks"
                @selectedMerchant:change="$emit('selectedMerchant:change', $event)"
                :order="order"
                :auth="auth"
                :merchants="merchants"
                :selected-merchant="selectedMerchant"
                :several-merchants="severalMerchants"
            />

            <PublicOfferFreePayment
                v-else-if="order.is_free"
                @handleAuth="handleAuth"
                @handlePaymentLinks="handlePaymentLinks"
                :order="order"
                :auth="auth"
            />

            <PublicOfferDefaultPayment
                v-else
                @handleAuth="handleAuth"
                @handlePaymentLinks="handlePaymentLinks"
                @change:paymentType="$emit('change:paymentType', $event)"
                @selectedMerchant:change="$emit('selectedMerchant:change', $event)"
                :order="order"
                :auth="auth"
                :show-partial-price="showPartialPrice"
                :payment-parts="paymentParts"
                :merchants="merchants"
                :payment-type="paymentType"
                :payment-update-loading="paymentUpdateLoading"
                :selected-merchant="selectedMerchant"
                :several-merchants="severalMerchants"
            />

            <PublicOfferPaymentError
                v-if="orderUpdateError || checkingErrorMessage"
                :message="orderUpdateError || checkingErrorMessage"
            />
        </div>
    </div>
</template>

<script>
import formatDate from "@mixins/formatDate"
import DefaultTitle from "@components/Typography/DefaultTitle"
import PublicOfferCouponInput from "./PublicOfferCouponInput"
import PublicOfferDefaultPayment from "./PublicOfferDefaultPayment.vue"
import PublicOfferFreePayment from "./PublicOfferFreePayment.vue"
import PublicOfferOrderItem from "./PublicOfferOrderItem"
import PublicOfferOrderDiscount from "./PublicOfferOrderDiscount"
import PublicOfferOrderPrice from "./PublicOfferOrderPrice.vue"
import PublicOfferPaymentError from "./PublicOfferPaymentError.vue"
import PublicOfferPrependPayment from "./PublicOfferPrependPayment.vue"
import AuthorizationDynamicFields from "@public-modal/components/flow/fields/AuthorizationDynamicFields"
import axios from "@libs/axios"
import PublicOfferPoints from "./PublicOfferPoints.vue"

export default {
    name: "PublicOfferOrder",
    components: {
        PublicOfferPaymentError,
        PublicOfferFreePayment,
        PublicOfferDefaultPayment,
        PublicOfferPrependPayment,
        PublicOfferOrderPrice,
        PublicOfferPoints,
        AuthorizationDynamicFields,
        PublicOfferOrderDiscount,
        PublicOfferOrderItem,
        PublicOfferCouponInput,
        DefaultTitle
    },
    mixins: [formatDate],
    props: {
        order: {
            type: Object,
            default: () => {},
            required: true
        },
        payment: {
            type: Object,
            default: () => {}
        },
        loading: {
            type: Boolean,
            default: false,
            required: false
        },
        auth: {
            type: Boolean
        },
        allowCoupons: {
            type: Boolean,
            default: false,
            required: false
        },
        allowPoints: {
            type: Boolean,
            default: false,
            required: false
        },
        showPaymentLinks: {
            type: Boolean,
            default: false
        },
        paymentType: {
            type: String,
            default: "full"
        },
        paymentUpdateLoading: {
            type: Boolean,
            default: false
        },
        paymentParts: Array,
        orderUpdateError: String,
        selectedMerchant: {
            type: Object,
            default: null
        }
    },
    inject: ["getPaymentTypes"],
    data() {
        return {
            paymentTypes: null,
            errors: {},
            message: "",
            paymentChecking: false,
            checkingErrorMessage: null,
            paymentsSimplifiedView: false,
            disablePaymentButton: false
        }
    },
    computed: {
        showPartialPrice() {
            return !!(
                (this.paymentType === this.paymentTypes.is_pay_by_equal_parts ||
                    this.paymentType === this.paymentTypes.is_pay_by_specified_schedule) &&
                this.paymentParts.length >= 1
            )
        },
        severalMerchants() {
            return this.merchants.length > 1
        },
        merchants() {
            if (this.payment) {
                //if (!this.payment.links.length || this.payment.links.length === 1) return null
                return this.payment.links
            } else {
                //if (!this.order.payment_links.length || this.order.payment_links.length === 1) return null
                return this.order.payment_links
            }
        },
        help() {
            return window.help_text || ""
        }
    },
    created() {
        this.paymentTypes = this.getPaymentTypes()
    },
    methods: {
        requiredFieldsNotFilled() {
            return this.order.fields && this.order.fields.filter(f => !f.value && f.is_required).length
        },
        handleAuth(token) {
            this.$emit("update", token)
        },
        handlePaymentAuth(token) {
            this.$emit("paymentAuth", token)
        },
        async handleLink(url) {
            let selectedUrl = url
                ? url
                : this.severalMerchants
                ? this.selectedMerchant.link
                : this.order.payment_links.length > 0
                ? this.order.payment_links[0]?.link
                : this.payment?.links?.length > 0
                ? this.payment.links[0].link
                : null

            if (!selectedUrl) {
                return
            }

            if (this.order.save_fields_endpoint) {
                try {
                    await axios.post(this.order.save_fields_endpoint, this.mapFieldsToPayload(), {
                        headers: {
                            "Content-Type": "multipart/form-data"
                        }
                    })
                    this.disablePaymentButton = true
                    console.log("disablePaymentButton:", this.disablePaymentButton)

                    window.fbq &&
                        window.fbq("track", "AddToCart", {}, { eventID: `AddToCart_order_id_${this.order.id}` })

                    window.open(selectedUrl, "_self")
                } catch (e) {
                    const { message, errors } = e.response.data
                    this.message = message
                    if (errors) {
                        this.errors = errors
                    } else {
                        this.errors = {}
                    }
                }
            } else {
                window.fbq && window.fbq("track", "AddToCart", {}, { eventID: `AddToCart_order_id_${this.order.id}` })
                window.ttq && window.ttq.track("AddToCart")

                window.open(selectedUrl, "_self")
            }
        },
        mapFieldsToPayload() {
            const formData = new FormData()
            this.order.fields.forEach(field => {
                if (
                    field.type === "timezone" ||
                    field.type === "currency" ||
                    field.type === "dropdown" ||
                    field.type === "country"
                ) {
                    if (field.value) {
                        formData.append(field.name, field.value.id)
                    }
                } else {
                    // eslint-disable-next-line no-empty
                    if (field.type === "phone" && field.value.length < 5 && !field.is_required) {
                    } else {
                        formData.append(field.name, field.value)
                    }
                }
            })
            formData.append("order_id", this.order.id)
            return formData
        },
        async handlePaymentLinks() {
            this.checkingErrorMessage = null
            this.paymentChecking = true
            try {
                let url = `/public/orders/${this.order.id}/check-valid`

                if (this.payment && this.payment.id) {
                    url += `?pid=${this.payment.id}`
                }

                await axios.get(url)

                let paymentLinks = this.merchants

                if (paymentLinks) {
                    if (paymentLinks.length === 1) {
                        await this.handleLink(paymentLinks[0].link)
                        return
                    } else if (this.severalMerchants && this.selectedMerchant) {
                        await this.handleLink(this.selectedMerchant.link)
                        return
                    }
                }

                this.$emit("show:links", true)
            } catch (e) {
                this.checkingErrorMessage = e.response.data.message
            } finally {
                this.paymentChecking = false
            }
        }
    }
}
</script>

<style lang="sass">
@import 'resources/sass/vars'
.amount-formatted
    font-weight: 600
    line-height: 21px
    margin-bottom: 1px

.partial-checkbox
    .custom-checkbox__label
        color: $text-color !important

.public-offer-terms
    font-size: 11px
    line-height: 130%
    color: $text-accent-color
    a
        color: $accent-color7
        border-bottom: 1px solid $accent-color7
    @media (max-width: $sxsMobile)
        text-align: center
</style>

<style scoped lang="sass">
@import 'resources/sass/vars'
$paymentCardWidth: 160px

.subscription-text
  font-weight: 400
  line-height: 130%
.subscription-text-md
  @extend .subscription-text
  font-size: 13px
  color: $text-accent-color
.subscription-text-sm
  @extend .subscription-text
  font-size: 10px
  color: $text-color2
.accent-date
  color: $accent-color5

.public-offer-order
    max-width: 450px
    width: 100%
    padding: 20px 27px 20px 20px
    transition: .3s
    height: 100%
    @media (max-width: 960px)
        padding: 20px
    @media (max-width: 640px)
        padding: 10px
    &__total
        text-align: right
    .discount
        text-decoration: line-through
        color: #B5C1D2
        font-weight: 500
</style>
